import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { Header } from '@/components/Header';
import { BottomNav, DesktopNav } from '@/components/BottomNav';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Rocket, Zap, Plus, BarChart3, Clock, Layers, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Play, Square, LayoutGrid, List, Shield, AlertCircle, Archive, ArchiveRestore, Ban, ChevronUp, ChevronDown, Target, Settings2, X, CheckSquare, Trash2, AlertTriangle, Filter, Search, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignalBotSettings } from '@/components/signal/SignalBotSettings';
import { SignalDailyStats } from '@/components/signal/SignalDailyStats';
import { SignalHistory } from '@/components/signal/SignalHistory';
import { SignalAutoSignals } from '@/components/signal/SignalAutoSignals';
import { SignalChartsTab } from '@/components/signal/SignalChartsTab';
import { store } from '@/store';
import { Provider } from 'react-redux';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";


export default function SignalBotPage(props: any) {
  return (
    <Provider store={store}>
      <SignalBotContent {...props} />
    </Provider>
  );
}

function parseHash(hash: string): string {
  const raw = hash.replace(/^#\/?/, '');
  if (raw === 'daily_stats' || raw === 'history' || raw === 'create' || raw === 'auto_signals' || raw === 'charts') return raw;
  return 'bots';
}

function SignalBotContent(props: any) {
  const { initial_bots = [], current_user, enabled_exchanges, scanner_monitoring = {}, initial_source, signal_enabled } = props;
  const [activeTab, setActiveTab] = useState('bots');
  const [activeSignalTab, setActiveSignalTabState] = useState(() => parseHash(window.location.hash)); // bots, daily_stats, history, create
  const defaultFilter = initial_source === 'auto_scanner' ? 'auto' : (initial_source === 'manual' ? 'manual' : 'all');
  const [botFilter, setBotFilter] = useState(defaultFilter); // all, manual, auto
  const [modeFilter, setModeFilter] = useState('all'); // all, live, testnet, simulation
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'card' | 'compact'>(() => (localStorage.getItem('signal_bot_view_mode') as 'card' | 'compact') || 'compact');
  const [sortBy, setSortBy] = useState(() => (localStorage.getItem('signal_bot_sort_by') || 'status'));
  const [isScannerOpen, setIsScannerOpen] = useState(() => {
    return localStorage.getItem('is_scanner_panel_open') !== 'false';
  });
  const [cooldowns, setCooldowns] = useState<any[]>([]);
  const [isCooldownCollapsed, setIsCooldownCollapsed] = useState(() => {
    return localStorage.getItem('cooldown_collapsed') === 'true';
  });

  const [batchUpdateOpen, setBatchUpdateOpen] = useState(false);
  const [batchFilters, setBatchFilters] = useState<{ strategy_type?: string[]; source?: string }>({ source: 'auto_scanner' });
  const [batchSettings, setBatchSettings] = useState<Record<string, any>>({});
  const [batchResult, setBatchResult] = useState<{ message: string; ok: boolean } | null>(null);
  const [batchUpdating, setBatchUpdating] = useState(false);
  const batchStrategyTypes = useMemo(() => {
    const types = new Set<string>();
    initial_bots.forEach((b: any) => { if (b.source === 'auto_scanner') types.add(b.strategy_type); });
    return Array.from(types).sort();
  }, [initial_bots]);

  const [batchMode, setBatchMode] = useState(false);
  const [selectedBotIds, setSelectedBotIds] = useState<Set<number>>(new Set());
  const [batchOpLoading, setBatchOpLoading] = useState(false);
  const [batchOpResult, setBatchOpResult] = useState<{ message: string; ok: boolean } | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState<{ name: string; symbols: string[]; strategies: string[]; intervals: string[]; position: '' | 'yes' | 'no'; archived: '' | 'yes' | 'no' }>({ name: '', symbols: [], strategies: [], intervals: [], position: '', archived: '' });

  const handleCooldownCollapsedChange = (collapsed: boolean) => {
    setIsCooldownCollapsed(collapsed);
    localStorage.setItem('cooldown_collapsed', String(collapsed));
  };

  const handleSortByChange = (val: string) => {
    setSortBy(val);
    localStorage.setItem('signal_bot_sort_by', val);
  };

  const isCreating = activeSignalTab === 'create';
  const setIsCreating = (val: boolean) => {
    setActiveSignalTab(val ? 'create' : 'bots');
  };

  const [newBotData, setNewBotData] = useState({
    name: 'New Signal Bot',
    symbol: 'BTCUSDT',
    exchange: 'binance',
    strategy_type: 'hull_macd',
    interval: '30m',
    leverage: 10,
    entry_amount: 100,
    principal_amount: 1000,
    entry_type: 'fixed',
    is_live: false,
    use_testnet: true,
    settings: {
      tp_pct: 1.0,
      sl_pct: 0.5
    }
  });

  const availableStrategies = useMemo(() => {
    const strategies = new Set<string>();
    initial_bots.forEach((b: any) => strategies.add(b.strategy_type));
    return Array.from(strategies).sort();
  }, [initial_bots]);

  const [showArchived, setShowArchived] = useState(() => {
    return localStorage.getItem('signal_bot_show_archived') === 'true';
  });

  const [showActiveOnly, setShowActiveOnly] = useState(() => {
    return localStorage.getItem('signal_bot_show_active_only') === 'true';
  });

  const [showPositionOnly, setShowPositionOnly] = useState(() => {
    return localStorage.getItem('signal_bot_show_position_only') === 'true';
  });

  const handleShowArchivedChange = (show: boolean) => {
    setShowArchived(show);
    localStorage.setItem('signal_bot_show_archived', String(show));
  };

  const handleShowActiveOnlyChange = (activeOnly: boolean) => {
    setShowActiveOnly(activeOnly);
    localStorage.setItem('signal_bot_show_active_only', String(activeOnly));
  };

  const handleShowPositionOnlyChange = (positionOnly: boolean) => {
    setShowPositionOnly(positionOnly);
    localStorage.setItem('signal_bot_show_position_only', String(positionOnly));
  };

  const BOTS_PER_PAGE = 25;
  const [botPage, setBotPage] = useState(1);

  const sortedBots = useMemo(() => {
    let filtered = initial_bots.filter((bot: any) => {
      // 아카이브 필터링 (기본적으로 숨김)
      if (!showArchived) {
        if (bot.archived === true || bot.archived === 1) return false;
      }

      // 가동 중인 봇만 보기 필터링
      if (showActiveOnly && !bot.is_active) return false;

      // 포지션 있는 봇만 보기 필터링
      if (showPositionOnly && (!bot.position || !bot.position.side)) return false;

      // 1. 봇 유형 필터링 (자동 생성 소스 기준)
      if (botFilter === 'manual') {
        if (bot.source === 'auto_scanner') return false;
      } else if (botFilter === 'auto') {
        if (bot.source !== 'auto_scanner') return false;
      }

      // 2. 계정 모드 필터링
      if (modeFilter === 'live') {
        return bot.is_live && !bot.use_testnet;
      } else if (modeFilter === 'testnet') {
        return bot.is_live && bot.use_testnet;
      } else if (modeFilter === 'simulation') {
        return !bot.is_live;
      }

      // 3. 전략 유형 필터링
      if (strategyFilter !== 'all' && bot.strategy_type !== strategyFilter) return false;

      return true; // all
    });

    return filtered.sort((a: any, b: any) => {
      if (sortBy === 'status') {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        if (a.is_live && !b.is_live) return -1;
        if (!a.is_live && b.is_live) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      if (sortBy === 'net_pnl_desc') {
        return Number(b.net_pnl || 0) - Number(a.net_pnl || 0);
      }
      if (sortBy === 'net_pnl_asc') {
        return Number(a.net_pnl || 0) - Number(b.net_pnl || 0);
      }
      if (sortBy === 'created_desc') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === 'created_asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });
  }, [initial_bots, botFilter, modeFilter, strategyFilter, sortBy, showArchived, showActiveOnly, showPositionOnly]);

  useEffect(() => { setBotPage(1); }, [botFilter, modeFilter, strategyFilter, sortBy, showArchived, showActiveOnly, showPositionOnly]);

  const totalBotPages = Math.ceil(sortedBots.length / BOTS_PER_PAGE);
  const paginatedBots = useMemo(() => sortedBots.slice((botPage - 1) * BOTS_PER_PAGE, botPage * BOTS_PER_PAGE), [sortedBots, botPage]);

  const batchFilterOptions = useMemo(() => {
    const symbols = new Set<string>();
    const strategies = new Set<string>();
    const intervals = new Set<string>();
    sortedBots.forEach((b: any) => {
      symbols.add(b.symbol);
      strategies.add(b.strategy_type);
      intervals.add(b.interval);
    });
    return {
      symbols: Array.from(symbols).sort(),
      strategies: Array.from(strategies).sort(),
      intervals: Array.from(intervals).sort(),
    };
  }, [sortedBots]);

  const selectBotsByFilters = useCallback((criteria: typeof filterCriteria) => {
    const ids = sortedBots.filter((b: any) => {
      if (criteria.name) {
        const q = criteria.name.toLowerCase();
        if (!b.name.toLowerCase().includes(q) && !b.symbol.toLowerCase().includes(q)) return false;
      }
      if (criteria.symbols.length > 0 && !criteria.symbols.includes(b.symbol)) return false;
      if (criteria.strategies.length > 0 && !criteria.strategies.includes(b.strategy_type)) return false;
      if (criteria.intervals.length > 0 && !criteria.intervals.includes(b.interval)) return false;
      if (criteria.position === 'yes' && !(b.position && b.position.qty > 0)) return false;
      if (criteria.position === 'no' && b.position && b.position.qty > 0) return false;
      if (criteria.archived === 'yes' && !b.archived) return false;
      if (criteria.archived === 'no' && b.archived) return false;
      return true;
    }).map((b: any) => b.id);
    setSelectedBotIds(new Set(ids));
    setBatchOpResult(null);
  }, [sortedBots]);

  const applyFiltersAndClose = useCallback(() => {
    selectBotsByFilters(filterCriteria);
    setFilterDialogOpen(false);
  }, [selectBotsByFilters, filterCriteria]);

  const toggleFilterItem = useCallback((key: 'symbols' | 'strategies' | 'intervals', value: string) => {
    setFilterCriteria(prev => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }, []);

  const toggleSelectBot = useCallback((id: number) => {
    setSelectedBotIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setBatchOpResult(null);
  }, []);

  const selectAllBots = useCallback(() => {
    const ids = sortedBots.map((b: any) => b.id);
    setSelectedBotIds(new Set(ids));
    setBatchOpResult(null);
  }, [sortedBots]);

  const deselectAllBots = useCallback(() => {
    setSelectedBotIds(new Set());
    setBatchOpResult(null);
  }, []);

  const handleBatchOperation = useCallback(async (action: 'start' | 'stop') => {
    const ids = Array.from(selectedBotIds);
    if (ids.length === 0) return;
    setBatchOpLoading(true);
    setBatchOpResult(null);
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const res = await fetch('/signal/batch_operation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ bot_ids: ids, action_type: action })
      });
      const data = await res.json();
      setBatchOpResult({ message: data.message, ok: data.ok });
      if (data.ok) {
        setSelectedBotIds(new Set());
        setTimeout(() => { router.reload({ only: ['initial_bots'] }); }, 1000);
      }
    } catch (err: any) {
      setBatchOpResult({ message: '요청 실패: ' + err.message, ok: false });
    } finally {
      setBatchOpLoading(false);
    }
  }, [selectedBotIds]);

  const setActiveSignalTab = (tab: string) => {
    setActiveSignalTabState(tab);
    window.history.pushState(null, '', `#${tab}`);
  };

  React.useEffect(() => {
    const onPopState = () => {
      setActiveSignalTabState(parseHash(window.location.hash));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const fetchCooldowns = async () => {
      try {
        const res = await fetch('/dca/cooldown_status');
        const data = await res.json();
        if (data.ok) {
          setCooldowns(data.cooldowns || []);
        }
      } catch (err) {
        console.error('Failed to fetch cooldown status:', err);
      }
    };

    fetchCooldowns();
    const interval = setInterval(fetchCooldowns, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (cooldowns.length === 0) return;
    const timer = setInterval(() => {
      setCooldowns(prev =>
        prev
          .map(c => ({ ...c, remaining_seconds: c.remaining_seconds - 1 }))
          .filter(c => c.remaining_seconds > 0)
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldowns.length]);

  const formatDuration = (seconds: number) => {
    if (seconds <= 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isCreating) return;
    const interval = setInterval(() => {
      router.reload({ only: ['initial_bots'], preserveUrl: true });
    }, 10000);
    return () => clearInterval(interval);
  }, [isCreating]);

  const handleNavChange = (tab: string) => {
    if (tab === 'dca') {
      window.location.href = '/dca';
    } else {
      setActiveTab(tab);
    }
  };

  const handleCreate = () => {
    router.post('/signal', { bot: newBotData }, {
      onSuccess: () => {
        setIsCreating(false);
      }
    });
  };

  const toggleBot = (id: number) => {
    const bot = initial_bots.find((b: any) => b.id === id);
    // 정지된 auto_scanner 봇: 설정 페이지로 이동 (설정 검토/수정 후 직접 시작)
    if (!bot.is_active && bot.source === 'auto_scanner') {
      router.get(`/signal/${id}#settings`);
      return;
    }
    const action = bot.is_active ? 'stop' : 'start';
    router.post(`/signal/${id}/${action}`, {}, {
      preserveScroll: true
    });
  };

  const handleArchive = (e: React.MouseEvent, botId: number) => {
    e.stopPropagation();
    if (confirm('이 봇을 보관하시겠습니까? 목록에서 숨겨집니다.')) {
      router.post(`/signal/${botId}/archive`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          router.reload({ only: ['initial_bots'] });
        }
      });
    }
  };

  const handleUnarchive = (e: React.MouseEvent, botId: number) => {
    e.stopPropagation();
    router.post(`/signal/${botId}/unarchive`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        router.reload({ only: ['initial_bots'] });
      }
    });
  };

  const handleViewModeChange = (mode: 'card' | 'compact') => {
    setViewMode(mode);
    localStorage.setItem('signal_bot_view_mode', mode);
  };



  return (
    <div className="min-h-screen bg-background font-sans antialiased text-foreground pb-24 md:pb-8 transition-colors duration-300">
      <div className="max-w-md md:max-w-7xl mx-auto w-full px-4 py-4 md:p-8 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <Header />
          <div className="flex items-center gap-2">
            <DesktopNav activeTab={activeTab} setActiveTab={handleNavChange} current_user={current_user} />
          </div>
        </div>

        <main className="space-y-6">
          {!signal_enabled && (
            <Alert variant="destructive" className="bg-danger/10 border-danger/20 text-danger rounded-2xl shadow-sm">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle className="font-black">Signal 기능 비활성화됨</AlertTitle>
              <AlertDescription className="font-bold text-xs opacity-80">
                현재 시스템 환경에서 Signal 봇 기능이 비활성화되어 있습니다. 새 봇 생성 및 가동이 차단됩니다.
              </AlertDescription>
            </Alert>
          )}

          {/* 쿨다운 / 서킷 브레이커 현황판 */}
          {cooldowns.length > 0 && (
            isCooldownCollapsed ? (
              <div
                onClick={() => handleCooldownCollapsedChange(false)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-danger/10 border border-danger/20 text-danger rounded-full text-xs font-black cursor-pointer hover:bg-danger/20 transition-all select-none animate-in fade-in"
              >
                <Ban className="w-3.5 h-3.5 text-danger animate-pulse shrink-0" />
                <span>차단 중 ({cooldowns.length})</span>
                <ChevronDown className="w-3.5 h-3.5 text-danger" />
              </div>
            ) : (
              <Card className="border-danger/20 bg-danger/5 shadow-md rounded-2xl overflow-hidden animate-in fade-in duration-300">
                <CardHeader
                  className="py-3 px-5 bg-danger/10 border-b border-danger/10 flex flex-row items-center justify-between cursor-pointer hover:bg-danger/20 transition-colors select-none"
                  onClick={() => handleCooldownCollapsedChange(true)}
                >
                  <div className="flex items-center gap-2">
                    <Ban className="w-4 h-4 text-danger animate-pulse" />
                    <h3 className="font-black text-xs text-danger uppercase tracking-wider">실시간 진입 차단 현황 (전역 Cooldown & Circuit Breaker)</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-danger/20 text-danger border-none text-[9px] font-black h-5">
                      {cooldowns.length}개 심볼
                    </Badge>
                    <ChevronUp className="w-4 h-4 text-danger" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 animate-in slide-in-from-top-2 duration-200">
                  {cooldowns.map((c) => (
                    <div key={`${c.symbol}:${c.type}`} className="flex items-center justify-between p-2.5 bg-card/65 border border-border/40 rounded-xl shadow-sm">
                      <div className="min-w-0 flex-1 pr-2">
                        <div className="flex items-center gap-1.5">
                          <span className="font-black text-xs text-foreground">{c.symbol}</span>
                          <Badge variant="outline" className={`text-[8px] font-black h-4 px-1 border-none ${c.type === 'circuit_breaker' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'}`}>
                            {c.type === 'circuit_breaker' ? 'BREAKER' : 'COOLDOWN'}
                          </Badge>
                        </div>
                        <p className="text-[10px] font-bold text-muted-foreground mt-0.5 truncate leading-tight">{c.message}</p>
                      </div>
                      <div className="flex items-center gap-1 bg-muted/60 px-2 py-1 rounded-lg text-foreground font-black text-xs tabular-nums shrink-0">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        {formatDuration(c.remaining_seconds)}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )
          )}

          {isCreating ? (
            <SignalBotSettings
              botData={newBotData}
              setBotData={setNewBotData}
              enabledExchanges={enabled_exchanges}
              onSave={handleCreate}
              onCancel={() => setIsCreating(false)}
            />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center justify-between w-full sm:w-auto gap-4">
                  <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
                    <Zap className="w-8 h-8 text-primary fill-primary/20" />
                    SIGNAL BOT <span className="text-muted-foreground font-normal text-sm ml-2">Beta</span>
                  </h1>
                </div>

                <div className="flex items-center justify-between w-full sm:w-auto gap-4">
                  <Tabs value={activeSignalTab} onValueChange={setActiveSignalTab} className="w-full sm:w-auto">
                    <TabsList className="bg-muted/50 p-1 rounded-2xl border border-border h-auto flex flex-wrap">
                      <TabsTrigger value="bots" className="rounded-xl py-2 px-4 font-bold text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <Layers className="w-3.5 h-3.5 mr-1.5" /> 봇 목록
                      </TabsTrigger>
                      <TabsTrigger value="history" className="rounded-xl py-2 px-4 font-bold text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <Clock className="w-3.5 h-3.5 mr-1.5" /> 체결 이력
                      </TabsTrigger>
                      {current_user?.profile?.receive_auto_signals && (
                        <TabsTrigger value="auto_signals" className="rounded-xl py-2 px-4 font-bold text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm">
                          autobot 신호
                        </TabsTrigger>
                      )}
                      <TabsTrigger value="daily_stats" className="rounded-xl py-2 px-4 font-bold text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> 일별 분석
                      </TabsTrigger>
                      <TabsTrigger value="charts" className="rounded-xl py-2 px-4 font-bold text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm">
                        <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> 차트 모아보기
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              {/* 조건부 렌더링 */}
              {activeSignalTab === 'daily_stats' ? (
                <SignalDailyStats />
              ) : activeSignalTab === 'auto_signals' && current_user?.profile?.receive_auto_signals ? (
                <SignalAutoSignals scannerMonitoring={scanner_monitoring} bots={initial_bots} />
              ) : activeSignalTab === 'history' ? (
                <SignalHistory />
              ) : activeSignalTab === 'charts' ? (
                <SignalChartsTab initial_bots={initial_bots} />
              ) : (
                /* 봇 목록 및 필터 바 */
                <div className="space-y-4">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-muted/30 p-1.5 rounded-2xl border border-border gap-2">
                    <div className="flex gap-1.5 flex-wrap">
                      <Button
                        onClick={() => setBotFilter('all')}
                        variant={botFilter === 'all' ? 'default' : 'ghost'}
                        className="rounded-xl py-1.5 px-4 font-bold text-xs h-8"
                      >
                        전체 봇 ({initial_bots.length})
                      </Button>
                      <Button
                        onClick={() => setBotFilter('manual')}
                        variant={botFilter === 'manual' ? 'default' : 'ghost'}
                        className="rounded-xl py-1.5 px-4 font-bold text-xs h-8"
                      >
                        🛠️ 수동/지표 봇 ({initial_bots.filter((b: any) => b.source !== 'auto_scanner').length})
                      </Button>
                      <Button
                        onClick={() => setBotFilter('auto')}
                        variant={botFilter === 'auto' ? 'default' : 'ghost'}
                        className="rounded-xl py-1.5 px-4 font-bold text-xs h-8"
                      >
                        🚀 자율 스캐너 봇 ({initial_bots.filter((b: any) => b.source === 'auto_scanner').length})
                      </Button>
                    </div>

                    <div className="flex gap-1.5 border-t lg:border-t-0 lg:border-l border-border/50 pt-2 lg:pt-0 lg:pl-2 ml-0 flex-wrap">
                      <Button
                        onClick={() => setModeFilter('all')}
                        variant={modeFilter === 'all' ? 'secondary' : 'ghost'}
                        className="rounded-xl py-1 px-3 font-bold text-[11px] h-7"
                      >
                        전체 모드
                      </Button>
                      <Button
                        onClick={() => setModeFilter('live')}
                        variant={modeFilter === 'live' ? 'secondary' : 'ghost'}
                        className="rounded-xl py-1 px-3 font-bold text-[11px] h-7 text-success"
                      >
                        Live (실매매)
                      </Button>
                      <Button
                        onClick={() => setModeFilter('testnet')}
                        variant={modeFilter === 'testnet' ? 'secondary' : 'ghost'}
                        className="rounded-xl py-1 px-3 font-bold text-[11px] h-7 text-primary"
                      >
                        Testnet (테스트)
                      </Button>
                      <Button
                        onClick={() => setModeFilter('simulation')}
                        variant={modeFilter === 'simulation' ? 'secondary' : 'ghost'}
                        className="rounded-xl py-1 px-3 font-bold text-[11px] h-7 text-amber-500"
                      >
                        Sim (시뮬레이션)
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-xl border border-border/50 w-fit">
                      <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest pl-2">전략</span>
                      <Select value={strategyFilter} onValueChange={setStrategyFilter}>
                        <SelectTrigger className="w-[150px] h-6 text-[9px] font-bold bg-card border-border rounded-lg shadow-sm focus:ring-1 focus:ring-primary">
                          <SelectValue placeholder="전체 전략" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="all">전체 전략</SelectItem>
                          {availableStrategies.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50">
                      <Button
                        onClick={() => handleViewModeChange('card')}
                        variant={viewMode === 'card' ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-lg w-8 h-7 p-0 ${viewMode === 'card' ? 'shadow-sm' : 'text-muted-foreground'}`}
                        title="카드 보기"
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        onClick={() => handleViewModeChange('compact')}
                        variant={viewMode === 'compact' ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-lg w-8 h-7 p-0 ${viewMode === 'compact' ? 'shadow-sm' : 'text-muted-foreground'}`}
                        title="Compact 보기"
                      >
                        <List className="w-3.5 h-3.5" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50">
                      <Button
                        variant={showActiveOnly ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-lg font-bold text-[9px] h-6 px-2 ${showActiveOnly ? 'shadow-sm' : 'text-muted-foreground'}`}
                        onClick={() => handleShowActiveOnlyChange(!showActiveOnly)}
                      >
                        <Play className="w-3 h-3 mr-0.5 fill-current" />
                        가동 中
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50">
                      <Button
                        variant={showPositionOnly ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-lg font-bold text-[9px] h-6 px-2 ${showPositionOnly ? 'shadow-sm' : 'text-muted-foreground'}`}
                        onClick={() => handleShowPositionOnlyChange(!showPositionOnly)}
                      >
                        <Target className="w-3 h-3 mr-0.5" />
                        포지션 有
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50">
                      <Button
                        variant={showArchived ? 'default' : 'ghost'}
                        size="sm"
                        className={`rounded-lg font-bold text-[9px] h-6 px-2 ${showArchived ? 'shadow-sm' : 'text-muted-foreground'}`}
                        onClick={() => handleShowArchivedChange(!showArchived)}
                      >
                        <Archive className="w-3 h-3 mr-0.5" />
                        {showArchived ? '숨기기' : '보관'}
                      </Button>
                    </div>

                  </div>

                  <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-xl border border-border/50 w-fit">
                    <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest pl-2">Sort</span>
                    <Select value={sortBy} onValueChange={handleSortByChange}>
                      <SelectTrigger className="w-[140px] h-6 text-[9px] font-bold bg-card border-border rounded-lg shadow-sm focus:ring-1 focus:ring-primary">
                        <SelectValue placeholder="정렬 방식" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="status">상태별 (가동 중 우선)</SelectItem>
                        <SelectItem value="net_pnl_desc">손익 높은 순 (PnL)</SelectItem>
                        <SelectItem value="net_pnl_asc">손익 낮은 순 (PnL)</SelectItem>
                        <SelectItem value="name_asc">이름순 (가나다/A-Z)</SelectItem>
                        <SelectItem value="name_desc">이름역순 (하파타/Z-A)</SelectItem>
                        <SelectItem value="created_desc">생성순 (최신순)</SelectItem>
                        <SelectItem value="created_asc">생성순 (오래된순)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="w-px h-5 bg-border mx-1" />
                    <Button
                      onClick={() => { setBatchMode(!batchMode); if (batchMode) setSelectedBotIds(new Set()); }}
                      size="sm"
                      variant={batchMode ? 'default' : 'ghost'}
                      className={`rounded-lg font-bold text-[9px] h-6 px-2 ${batchMode ? 'shadow-sm' : 'text-muted-foreground'}`}
                    >
                      <CheckSquare className="w-3 h-3 mr-0.5" />
                      일괄관리
                    </Button>
                  </div>

                  {botFilter === 'auto' && (
                    <>
                      <div className="bg-purple-500/10 border border-purple-500/20 p-4 rounded-2xl flex items-start gap-3 animate-in fade-in duration-300">
                        <Rocket className="w-5 h-5 text-purple-500 shrink-0 mt-0.5 animate-bounce" />
                        <div>
                          <h4 className="font-black text-purple-600 text-sm">자율형 스캐너 봇 가동 안내</h4>
                          <p className="text-xs text-muted-foreground font-medium mt-1 leading-relaxed">
                            이 탭에 표시되는 봇들은 회원님이 직접 생성하는 것이 아니라, 백그라운드의 <strong className="text-foreground">MultiSymbolScannerWorker</strong>가 3분마다 시장을 감시하다가 최적의 타점(진입/SL/TP)이 포착되었을 때 자율적으로 생성하고 기동하는 봇들입니다. 스캐너 가동 여부 및 감시 코인 설정은 <a href="/admin" className="text-primary underline font-bold">관리자 대시보드(/admin)</a>에서 조정하실 수 있습니다.
                          </p>
                        </div>
                      </div>

                      <Card className="border border-border shadow-md rounded-2xl overflow-hidden bg-card animate-in fade-in duration-500">
                        <CardHeader
                          className="bg-muted/30 pb-3 border-b border-border cursor-pointer select-none hover:bg-muted/50 transition-colors flex flex-row items-center justify-between"
                          onClick={() => {
                            const nextState = !isScannerOpen;
                            setIsScannerOpen(nextState);
                            localStorage.setItem('is_scanner_panel_open', String(nextState));
                          }}
                        >
                          <CardTitle className="text-sm font-black flex items-center gap-2 text-foreground">
                            <Zap className="w-4 h-4 text-purple-500" /> 실시간 스캐너 정찰대 감시 현황판
                          </CardTitle>
                          <div className="text-muted-foreground">
                            {isScannerOpen ? (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </div>
                        </CardHeader>
                        {isScannerOpen && (
                          <CardContent className="p-0">
                            {Object.keys(scanner_monitoring).length === 0 ? (
                              <div className="p-8 text-center text-xs text-muted-foreground font-medium">
                                현재 집계된 스캔 현황이 없습니다. (스캐너가 3분 주기 스캔을 완료하면 자동으로 표시됩니다)
                              </div>
                            ) : (
                              <div className="divide-y divide-border">
                                {Object.entries(scanner_monitoring).map(([sym, data]: any) => {
                                  // 이 심볼을 다루는 자율형 스캐너 봇 매칭
                                  const matchBot = initial_bots.find((b: any) =>
                                    b.symbol === sym &&
                                    b.source === 'auto_scanner'
                                  );

                                  const hasPosition = matchBot?.position && matchBot.position.qty > 0;
                                  const currentPnl = matchBot?.current_pnl || 0;
                                  const currentPnlPct = matchBot?.current_pnl_pct || 0;
                                  const tpTargets = matchBot?.settings?.take_profit_targets || [];
                                  const executedTps = tpTargets.filter((tp: any) => tp.executed || tp['executed']).length;
                                  const totalTps = tpTargets.length;

                                  return (
                                    <div
                                      key={sym}
                                      onClick={() => {
                                        if (matchBot) {
                                          router.get(`/signal/${matchBot.id}`);
                                        }
                                      }}
                                      className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 hover:bg-muted/10 transition-colors gap-2 ${matchBot ? 'cursor-pointer hover:bg-muted/20' : ''}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full ${hasPosition ? 'bg-emerald-500 animate-ping' : 'bg-purple-500 animate-ping'}`} />
                                        <div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="font-black text-sm text-foreground">{sym}</span>
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase">{matchBot?.exchange || '--'}</span>
                                            {hasPosition && (
                                              <span className={`text-[9px] font-black px-1.5 py-0.2 rounded ${matchBot.position.side === 'long' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>
                                                {matchBot.position.side?.toUpperCase()}
                                              </span>
                                            )}
                                          </div>
                                          {hasPosition && matchBot.position.entry_at && (
                                            <div className="text-[9px] text-muted-foreground font-bold">
                                              시작: {new Date(matchBot.position.entry_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-6 w-full sm:w-auto justify-between sm:justify-end">
                                        {hasPosition && (
                                          <div className="text-right">
                                            <div className={`text-xs font-black ${currentPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                              {currentPnl >= 0 ? '+' : ''}{currentPnl.toFixed(2)} USDT
                                            </div>
                                            <div className={`text-[10px] font-black ${currentPnlPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                              ROE: {currentPnlPct >= 0 ? '+' : ''}{currentPnlPct.toFixed(1)}%
                                            </div>
                                          </div>
                                        )}

                                        {hasPosition && totalTps > 0 && (
                                          <div className="text-right">
                                            <div className="text-xs font-black text-primary">
                                              TP {executedTps}/{totalTps} 단계
                                            </div>
                                            <div className="text-[9px] text-muted-foreground font-bold">
                                              익절 진행상황
                                            </div>
                                          </div>
                                        )}

                                        <div className="text-right">
                                          <div className="text-xs font-black text-foreground">${data.price ? data.price.toLocaleString() : '0.00'}</div>
                                          <div className="text-[9px] text-muted-foreground font-bold">15m Close</div>
                                        </div>
                                        <div className="text-right min-w-[140px]">
                                          <div className={`text-xs font-bold truncate ${hasPosition ? 'text-emerald-600 font-black animate-pulse' : data.status?.includes('포착') ? 'text-success font-black animate-pulse' : 'text-muted-foreground'}`}>
                                            {hasPosition ? `🚀 포지션 운영 중` : (data.status || '탐색 중')}
                                          </div>
                                          <div className="text-[9px] text-muted-foreground italic">Updated: {data.updated_at || 'N/A'}</div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    </>
                  )}

                  {/* Batch Operations Toolbar */}
                  {batchMode && (
                    <div className="bg-card border border-border/50 rounded-2xl p-3 shadow-md animate-in slide-in-from-top-2 duration-200">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="flex items-center gap-2 text-sm font-black text-foreground shrink-0">
                          <CheckSquare className="w-4 h-4 text-primary" />
                          <span>{selectedBotIds.size > 0 ? `${selectedBotIds.size}개 선택됨` : '봇을 선택하세요'}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            onClick={selectAllBots}
                            disabled={batchOpLoading}
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-black rounded-xl"
                          >
                            <CheckSquare className="w-3 h-3 mr-1" /> 전체 선택
                          </Button>
                          {selectedBotIds.size > 0 && (
                            <Button
                              onClick={deselectAllBots}
                              disabled={batchOpLoading}
                              size="sm"
                              variant="ghost"
                              className="h-8 text-xs font-black rounded-xl text-muted-foreground hover:text-foreground"
                            >
                              <X className="w-3 h-3 mr-1" /> 선택 해제
                            </Button>
                          )}
                          <Button
                            onClick={() => setFilterDialogOpen(true)}
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-black rounded-xl text-primary border-primary/30 hover:bg-primary/10"
                          >
                            <Filter className="w-3 h-3 mr-1" /> 조건 선택
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-px h-6 bg-border mx-1" />
                          {selectedBotIds.size > 0 && (
                            <>
                              <Button
                                onClick={() => handleBatchOperation('start')}
                                disabled={batchOpLoading}
                                size="sm"
                                className="h-8 text-xs font-black rounded-xl bg-primary hover:bg-primary/90"
                              >
                                <Play className="w-3 h-3 mr-1 fill-current" /> 시작
                              </Button>
                              <Button
                                onClick={() => handleBatchOperation('stop')}
                                disabled={batchOpLoading}
                                size="sm"
                                variant="destructive"
                                className="h-8 text-xs font-black rounded-xl"
                              >
                                <Square className="w-3 h-3 mr-1 fill-current" /> 중지
                              </Button>
                              <Button
                                onClick={() => {
                                  if (confirm(`선택한 ${selectedBotIds.size}개 봇을 청산하시겠습니까?`)) {
                                    handleBatchOperation('liquidate');
                                  }
                                }}
                                disabled={batchOpLoading}
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs font-black rounded-xl text-amber-600 border-amber-500/30 hover:bg-amber-500/10"
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" /> 청산
                              </Button>
                              <Button
                                onClick={() => handleBatchOperation('archive')}
                                disabled={batchOpLoading}
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs font-black rounded-xl text-muted-foreground"
                              >
                                <Archive className="w-3 h-3 mr-1" /> 보관
                              </Button>
                              <Button
                                onClick={() => handleBatchOperation('unarchive')}
                                disabled={batchOpLoading}
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs font-black rounded-xl text-muted-foreground"
                              >
                                <ArchiveRestore className="w-3 h-3 mr-1" /> 보관 해제
                              </Button>
                              <Button
                                onClick={() => {
                                  if (confirm(`⚠️ 선택한 ${selectedBotIds.size}개 봇을 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다!`)) {
                                    handleBatchOperation('destroy');
                                  }
                                }}
                                disabled={batchOpLoading}
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs font-black rounded-xl text-red-500 border-red-500/30 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-3 h-3 mr-1" /> 삭제
                              </Button>
                              <div className="w-px h-6 bg-border mx-1" />
                              <Button
                                onClick={() => { setBatchUpdateOpen(true); setBatchResult(null); setBatchSettings({}); }}
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs font-black rounded-xl text-amber-600 border-amber-500/30 hover:bg-amber-500/10"
                              >
                                <Settings2 className="w-3 h-3 mr-1" /> 설정 일괄 수정
                              </Button>
                            </>
                          )}
                        </div>
                        {batchOpResult && (
                          <div className={`text-xs font-bold ${batchOpResult.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                            {batchOpResult.message}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={viewMode === 'card' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
                    {paginatedBots.map((bot: any) =>
                      viewMode === 'card' ? (
                        <Card key={bot.id} className={`overflow-hidden hover:shadow-lg transition-shadow border-border/50 bg-card group ${bot.archived ? 'opacity-70 bg-muted/20' : ''} ${batchMode && selectedBotIds.has(bot.id) ? 'ring-2 ring-primary/50' : ''}`}>
                          <CardHeader className="pb-2">
                            <div className="flex justify-between items-start">
                              <div className="flex items-start gap-2">
                                {batchMode && (
                                  <div onClick={(e) => { e.stopPropagation(); toggleSelectBot(bot.id); }} className="pt-0.5 shrink-0">
                                    <div className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${selectedBotIds.has(bot.id) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30 hover:border-primary/50'}`}>
                                      {selectedBotIds.has(bot.id) && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div>
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded uppercase tracking-tighter">{bot.exchange}</span>
                                  <span className="text-sm font-black tracking-tight">{bot.symbol}</span>
                                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm ${!bot.is_live ? 'bg-amber-500/10 text-amber-600' : bot.use_testnet ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`}>
                                    {!bot.is_live ? 'SIM' : bot.use_testnet ? 'TEST' : 'LIVE'}
                                  </span>
                                  {bot.source === 'auto_scanner' && (
                                    <span className="text-[9px] font-black bg-purple-500/10 text-purple-600 px-1.5 py-0.5 rounded shadow-sm animate-pulse">
                                      🚀 AUTO SCANNER
                                    </span>
                                  )}
                                  {!!bot.archived && (
                                    <span className="text-[9px] font-black bg-muted text-muted-foreground px-1.5 py-0.5 rounded shadow-sm">
                                      보관됨
                                    </span>
                                  )}
                                </div>
                                <CardTitle className="text-lg font-black flex items-center gap-1.5 flex-wrap">
                                  {bot.name}
                                  <span className="text-[11px] font-normal text-muted-foreground">#{bot.id}</span>
                                </CardTitle>
                              </div>
                              <div className={`w-3 h-3 rounded-full ${bot.is_active ? 'bg-green-500 animate-pulse' : 'bg-muted'} shadow-sm`} />
                            </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2 mb-4">
                              <div className="flex justify-between text-[11px] gap-2">
                                <span className="text-muted-foreground font-bold">진입 금액</span>
                                <span className="font-black text-foreground text-right">{bot.formatted_entry_amount} USDT <span className="text-muted-foreground font-bold ml-1">(x{bot.leverage})</span></span>
                              </div>
                              <div className="flex justify-between text-[10px] gap-2">
                                <span className="text-muted-foreground font-bold">생성 시간</span>
                                <span className="font-bold text-foreground/80 text-right">{bot.created_at}</span>
                              </div>
                              <div className="flex justify-between text-[10px] gap-2">
                                <span className="text-muted-foreground font-bold">봉/전략</span>
                                <span className="font-black text-foreground text-right">
                                  {bot.interval} / {bot.strategy_type === 'hull_macd' ? 'H-MACD' : bot.strategy_type === 'zerolag_macd' ? 'ZL-MACD' : bot.strategy_type === 'cen_ichi_kewma' ? 'CenIchi' : bot.strategy_type === 'ma_angle_breakout' ? 'MA-Angle' : bot.strategy_type === 'auto4_signal' ? 'Auto4' : bot.strategy_type === 'hybrid_pullback' ? 'Pullback' : bot.strategy_type}
                                </span>
                              </div>

                              {bot.position && bot.position.qty > 0 && (
                                <div className="flex justify-between text-[11px] bg-primary/5 p-1.5 rounded border border-primary/10 mt-1.5 gap-2">
                                  <span className="text-primary font-black flex items-center gap-1">🎯 평가 손익</span>
                                  <span className={`font-black text-right ${bot.current_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                    {bot.current_pnl >= 0 ? '+' : ''}{bot.current_pnl.toFixed(2)} USDT ({bot.current_pnl_pct >= 0 ? '+' : ''}{bot.current_pnl_pct.toFixed(2)}%)
                                  </span>
                                </div>
                              )}
                              {bot.position?.tp_targets && bot.position.tp_targets.some((t: any) => t.executed) && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(bot.position.tp_targets as any[]).filter((t: any) => t.executed).map((t: any, i: number) => (
                                    <span key={i} className="text-[9px] font-black bg-success/10 text-success border border-success/20 px-1.5 py-0.5 rounded">
                                      ✅ 반익절 {t.pct}%
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div className="flex justify-between text-[11px] pt-1.5 border-t border-border/30 gap-2">
                                <span className="text-muted-foreground font-bold">누적 손익 (Net)</span>
                                <span className={`font-black text-right ${bot.net_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                  {bot.net_pnl >= 0 ? '+' : ''}{bot.net_pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                                </span>
                              </div>

                              <div className="flex justify-between text-[11px] gap-2">
                                <span className="text-muted-foreground font-bold">총 수수료 (Fee)</span>
                                <span className="font-black text-foreground/80 text-right">
                                  {bot.total_fees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                                </span>
                              </div>

                              {bot.is_active && bot.settings?.use_auto_stop && bot.last_started_at && (
                                <div className="bg-amber-500/5 p-2 rounded-lg border border-amber-500/10 mt-1">
                                  <div className="flex justify-between items-center text-[10px] gap-2">
                                    <span className="text-amber-600 font-black flex items-center gap-1">
                                      <Rocket className="w-3 h-3" /> 남은 가동 시간
                                    </span>
                                    <span className="text-amber-700 font-black text-right">
                                      {(() => {
                                        const startedAt = new Date(bot.last_started_at).getTime();
                                        const now = new Date().getTime();
                                        const remaining = Math.max(0, (startedAt + 24 * 60 * 60 * 1000) - now);
                                        const hours = Math.floor(remaining / (1000 * 60 * 60));
                                        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                                        return `${hours}시간 ${minutes}분`;
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {bot.cooldown_remaining > 0 ? (
                                <div className="mt-1 pt-1 border-t border-border/20 bg-red-500/5 p-1 rounded">
                                  <p className="text-[10px] font-black text-red-500 animate-pulse leading-tight">
                                    ⚠️ 수동 청산 쿨다운 중 ({bot.cooldown_remaining}초 남음)
                                  </p>
                                </div>
                              ) : bot.status && (
                                <div className="mt-1 pt-1 border-t border-border/20">
                                  <p className="text-[10px] font-bold text-primary truncate leading-tight">
                                    {bot.status}
                                  </p>
                                </div>
                              )}
                              <div className="flex justify-between text-[11px] gap-2">
                                <span className="text-muted-foreground font-bold">상태</span>
                                <span className={`font-black uppercase text-right ${bot.realtime_status === 'running' ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {bot.realtime_status}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex flex-col gap-1.5">
                                <Button
                                  onClick={() => router.get(`/signal/${bot.id}`)}
                                  variant="outline"
                                  className="w-full font-bold h-9 border-border hover:bg-muted/50"
                                >
                                  상세 보기
                                </Button>
                                {bot.is_active && bot.settings?.use_auto_stop && (
                                  <Button
                                    onClick={() => router.post(`/signal/${bot.id}/extend`)}
                                    variant="ghost"
                                    className="w-full font-black text-[10px] h-7 text-amber-600 hover:bg-amber-500/10 border border-amber-500/20"
                                  >
                                    24h 연장하기
                                  </Button>
                                )}
                              </div>
                              {bot.archived ? (
                                <Button
                                  onClick={(e) => handleUnarchive(e, bot.id)}
                                  variant="outline"
                                  className="w-full font-black h-full shadow-md text-primary hover:bg-primary/10 border-primary/20"
                                >
                                  <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> 복구
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => toggleBot(bot.id)}
                                  disabled={!signal_enabled && !bot.is_active}
                                  variant={bot.is_active ? "destructive" : "default"}
                                  className={`w-full font-black h-full shadow-md transition-all ${bot.is_active ? 'bg-danger hover:bg-danger/90' : 'bg-primary hover:scale-[1.02]'}`}
                                >
                                  {bot.is_active ? '중지' : '시작'}
                                </Button>
                              )}
                            </div>
                            {!bot.is_active && !bot.archived && (
                              <div className="mt-2 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleArchive(e, bot.id)}
                                  className="h-7 text-[10px] font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 p-0"
                                >
                                  <Archive className="w-3.5 h-3.5 mr-1 inline" /> 보관하기
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : (
                        <Card
                          key={bot.id}
                          onClick={() => router.get(`/signal/${bot.id}`)}
                          className={`overflow-hidden rounded-2xl border-border/50 hover:bg-muted/10 transition-colors group cursor-pointer shadow-sm ${bot.archived ? 'opacity-70 bg-muted/20 shadow-none' : 'bg-card'} ${batchMode && selectedBotIds.has(bot.id) ? 'ring-2 ring-primary/50' : ''}`}
                        >
                          <CardContent className="p-3 md:p-4">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                              <div className="flex items-center gap-3 min-w-0 lg:min-w-[260px]">
                                {batchMode && (
                                  <div onClick={(e) => { e.stopPropagation(); toggleSelectBot(bot.id); }} className="shrink-0">
                                    <div className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${selectedBotIds.has(bot.id) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30 hover:border-primary/50'}`}>
                                      {selectedBotIds.has(bot.id) && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div className={`w-1.5 h-9 rounded-full shrink-0 ${bot.is_active ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.35)]' : 'bg-muted'}`} />
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="font-black text-sm text-foreground truncate">{bot.symbol}</span>
                                    <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-black border-none bg-primary/10 text-primary uppercase">{bot.exchange}</Badge>
                                    <Badge variant="outline" className={`h-4 px-1.5 text-[9px] font-black border-none uppercase ${!bot.is_live ? 'bg-amber-500/10 text-amber-600' : bot.use_testnet ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`}>
                                      {!bot.is_live ? 'SIM' : bot.use_testnet ? 'TEST' : 'LIVE'}
                                    </Badge>
                                    {bot.source === 'auto_scanner' && (
                                      <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-black border-none bg-purple-500/10 text-purple-600 uppercase">AUTO</Badge>
                                    )}
                                    {!!bot.archived && (
                                      <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-black border-none bg-muted text-muted-foreground uppercase">보관됨</Badge>
                                    )}
                                    <span className="text-[10px] text-muted-foreground font-bold">#{bot.id}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-muted-foreground font-bold truncate">{bot.name}</span>
                                    <span className="text-[10px] text-muted-foreground/70 font-bold">{bot.interval}</span>
                                    <span className="text-[10px] text-muted-foreground/70 font-bold">
                                      {bot.strategy_type === 'hull_macd' ? 'H-MACD' : bot.strategy_type === 'zerolag_macd' ? 'ZL-MACD' : bot.strategy_type === 'cen_ichi_kewma' ? 'CenIchi' : bot.strategy_type === 'ma_angle_breakout' ? 'MA-Angle' : bot.strategy_type === 'auto4_signal' ? 'Auto4' : bot.strategy_type === 'hybrid_pullback' ? 'Pullback' : bot.strategy_type}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex lg:items-center gap-2 lg:gap-6 flex-1">
                                <div className="min-w-0">
                                  <div className="text-[9px] font-bold uppercase text-muted-foreground leading-none">Entry</div>
                                  <div className="text-[11px] font-black text-foreground tabular-nums">{bot.formatted_entry_amount} USDT</div>
                                </div>

                                <div className="min-w-0">
                                  <div className="text-[9px] font-bold uppercase text-muted-foreground leading-none">Net PnL</div>
                                  <div className={`text-[11px] font-black tabular-nums ${bot.net_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                    {bot.net_pnl >= 0 ? '+' : ''}{bot.net_pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                                  </div>
                                </div>

                                <div className="min-w-0">
                                  <div className="text-[9px] font-bold uppercase text-muted-foreground leading-none">Fee</div>
                                  <div className="text-[11px] font-black text-foreground/80 tabular-nums">
                                    {bot.total_fees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                                  </div>
                                </div>

                                <div className="min-w-0">
                                  <div className="text-[9px] font-bold uppercase text-muted-foreground leading-none">State</div>
                                  <div className={`text-[11px] font-black uppercase ${bot.realtime_status === 'running' ? 'text-primary' : 'text-muted-foreground'}`}>
                                    {bot.is_active ? 'RUNNING' : 'IDLE'}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between lg:justify-end gap-2 shrink-0">
                                <div className={`w-2.5 h-2.5 rounded-full ${bot.is_active ? 'bg-emerald-500 animate-pulse' : 'bg-muted'}`} />
                                <div className="flex items-center gap-1.5">
                                  {bot.is_active && bot.settings?.use_auto_stop && (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        router.post(`/signal/${bot.id}/extend`);
                                      }}
                                      variant="ghost"
                                      className="h-7 px-2.5 text-[10px] font-black text-amber-600 hover:bg-amber-500/10 border border-amber-500/20 rounded-xl"
                                    >
                                      24h
                                    </Button>
                                  )}
                                  {bot.archived ? (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchive(e, bot.id);
                                      }}
                                      variant="outline"
                                      className="h-7 px-3 text-[10px] font-black rounded-xl text-primary hover:bg-primary/10 border-primary/20"
                                    >
                                      <ArchiveRestore className="w-3 h-3 mr-1" /> 복구
                                    </Button>
                                  ) : (
                                    <>
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleBot(bot.id);
                                        }}
                                        disabled={!signal_enabled && !bot.is_active}
                                        variant={bot.is_active ? "destructive" : "default"}
                                        className={`h-7 px-3 text-[10px] font-black rounded-xl ${bot.is_active ? 'bg-danger hover:bg-danger/90' : 'bg-primary hover:bg-primary/90'}`}
                                      >
                                        {bot.is_active ? <Square className="w-3 h-3 mr-1 fill-current" /> : <Play className="w-3 h-3 mr-1 fill-current" />}
                                        {bot.is_active ? '중지' : '시작'}
                                      </Button>
                                      {!bot.is_active && (
                                        <Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleArchive(e, bot.id);
                                          }}
                                          variant="ghost"
                                          className="h-7 w-7 rounded-xl p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center"
                                          title="보관하기"
                                        >
                                          <Archive className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                    </>
                                  )}
                                  <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors hidden sm:block" />
                                </div>
                              </div>
                            </div>

                            {bot.position && bot.position.qty > 0 && (
                              <div className="mt-3 pt-3 border-t border-border/40 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-primary">
                                  <span className="uppercase">PnL</span>
                                  <span className={`font-black ${bot.current_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                    {bot.current_pnl >= 0 ? '+' : ''}{bot.current_pnl.toFixed(2)} USDT ({bot.current_pnl_pct >= 0 ? '+' : ''}{bot.current_pnl_pct.toFixed(2)}%)
                                  </span>
                                </div>
                                {bot.settings?.use_auto_stop && bot.last_started_at && (
                                  <div className="text-[10px] font-bold text-amber-600">
                                    남은 가동 시간: {(() => {
                                      const startedAt = new Date(bot.last_started_at).getTime();
                                      const now = new Date().getTime();
                                      const remaining = Math.max(0, (startedAt + 24 * 60 * 60 * 1000) - now);
                                      const hours = Math.floor(remaining / (1000 * 60 * 60));
                                      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                                      return `${hours}시간 ${minutes}분`;
                                    })()}
                                  </div>
                                )}
                                {bot.cooldown_remaining > 0 ? (
                                  <div className="text-[10px] text-red-500 font-black animate-pulse">⚠️ 수동 청산 쿨다운 중 ({bot.cooldown_remaining}초 남음)</div>
                                ) : bot.status && (
                                  <div className="text-[10px] text-muted-foreground font-bold truncate">{bot.status}</div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )
                    )}

                    {sortedBots.length > BOTS_PER_PAGE && (
                      <div className="col-span-full flex items-center justify-between px-4 py-3 bg-muted/10 rounded-2xl border border-border mt-2">
                        <div className="text-[11px] text-muted-foreground font-black">
                          총 {sortedBots.length}개 중 {(botPage - 1) * BOTS_PER_PAGE + 1}~{Math.min(botPage * BOTS_PER_PAGE, sortedBots.length)} 표시
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => setBotPage(1)} disabled={botPage === 1} className="h-8 w-8 p-0 rounded-xl border-border">
                            <ChevronsLeft className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setBotPage(p => Math.max(1, p - 1))} disabled={botPage === 1} className="h-8 w-8 p-0 rounded-xl border-border">
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </Button>
                          <div className="flex items-center justify-center h-8 px-3 bg-background border border-border rounded-xl text-[11px] font-black text-primary min-w-[70px]">
                            {botPage} / {totalBotPages}
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setBotPage(p => Math.min(totalBotPages, p + 1))} disabled={botPage >= totalBotPages} className="h-8 w-8 p-0 rounded-xl border-border">
                            <ChevronRight className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setBotPage(totalBotPages)} disabled={botPage >= totalBotPages} className="h-8 w-8 p-0 rounded-xl border-border">
                            <ChevronsRight className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <Card
                      onClick={() => {
                        if (signal_enabled) {
                          setIsCreating(true);
                        }
                      }}
                      className={
                        (!props.signal_enabled ? 'opacity-50 cursor-not-allowed ' : 'cursor-pointer hover:bg-primary/10 ') +
                        (viewMode === 'card' ? 'border-dashed border-2 border-primary/30 bg-primary/5 transition-all group flex flex-col items-center justify-center min-h-[200px]' : 'border-dashed border-2 border-primary/30 bg-primary/5 transition-all group flex flex-col items-center justify-center min-h-[96px] rounded-2xl')
                      }
                    >
                      {viewMode === 'card' ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Plus className="w-6 h-6 text-primary" />
                          </div>
                          <div className="text-center">
                            <h3 className="font-black text-primary">새 시그널 봇 만들기</h3>
                            <p className="text-[10px] text-muted-foreground font-medium mt-1">
                              {signal_enabled ? '클릭하여 전략 설정을 시작하세요' : '기능 비활성화됨'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2.5 px-3 py-2">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Plus className="w-4 h-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <h3 className="font-black text-primary text-sm">새 시그널 봇 만들기</h3>
                            <p className="text-[10px] text-muted-foreground font-medium"></p>
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Batch Update Modal */}
      {batchUpdateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-200" onClick={() => setBatchUpdateOpen(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-card z-10 flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-black flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-amber-500" />
                설정 일괄 수정
                {selectedBotIds.size > 0 && (
                  <span className="text-[10px] font-bold bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full ml-2">
                    {selectedBotIds.size}개 선택
                  </span>
                )}
              </h2>
              <button onClick={() => setBatchUpdateOpen(false)} className="p-1 hover:bg-muted rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {selectedBotIds.size === 0 ? (
                /* Strategy Type Filter — 선택된 봇 없을 때만 */
                <div className="space-y-2">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase">대상 전략 (복수 선택)</Label>
                  <div className="flex flex-wrap gap-2">
                    {batchStrategyTypes.map((st) => (
                      <button
                        key={st}
                        onClick={() => {
                          setBatchFilters(prev => {
                            const current = prev.strategy_type || [];
                            const next = current.includes(st) ? current.filter(s => s !== st) : [...current, st];
                            return { ...prev, strategy_type: next.length > 0 ? next : undefined };
                          });
                        }}
                        className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg border transition-all ${
                          (batchFilters.strategy_type || []).includes(st)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-muted'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-muted-foreground">선택 안 하면 모든 전략이 대상입니다</p>
                </div>
              ) : (
                /* 선택된 봇 정보 */
                <div className="bg-primary/5 border border-primary/10 rounded-xl p-3">
                  <p className="text-xs font-black text-foreground">{selectedBotIds.size}개 봇의 설정을 일괄 수정합니다</p>
                </div>
              )}

              <div className={selectedBotIds.size === 0 ? 'border-t border-border pt-4 space-y-4' : 'space-y-4'}>
                <h3 className="text-[11px] font-black text-muted-foreground uppercase tracking-wider">수정할 설정</h3>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold">손절 (SL%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="변경 안 함"
                      value={batchSettings.sl_pct ?? ''}
                      onChange={(e) => setBatchSettings(prev => ({ ...prev, sl_pct: e.target.value ? Number(e.target.value) : '' }))}
                      className="h-8 text-xs font-bold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold">익절 (TP%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="변경 안 함"
                      value={batchSettings.tp_pct ?? ''}
                      onChange={(e) => setBatchSettings(prev => ({ ...prev, tp_pct: e.target.value ? Number(e.target.value) : '' }))}
                      className="h-8 text-xs font-bold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold">레버리지 (x)</Label>
                    <Input
                      type="number"
                      step="1"
                      placeholder="변경 안 함"
                      value={batchSettings.leverage ?? ''}
                      onChange={(e) => setBatchSettings(prev => ({ ...prev, leverage: e.target.value ? Number(e.target.value) : '' }))}
                      className="h-8 text-xs font-bold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold">진입 금액 (USDT)</Label>
                    <Input
                      type="number"
                      step="10"
                      placeholder="변경 안 함"
                      value={batchSettings.entry_amount ?? ''}
                      onChange={(e) => setBatchSettings(prev => ({ ...prev, entry_amount: e.target.value ? Number(e.target.value) : '' }))}
                      className="h-8 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {batchResult && (
                <div className={`p-3 rounded-xl text-xs font-bold ${
                  batchResult.ok ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-red-500/10 text-red-600 border border-red-500/20'
                }`}>
                  {batchResult.message}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 h-9 text-xs font-bold rounded-xl"
                  onClick={() => setBatchUpdateOpen(false)}
                >
                  취소
                </Button>
                <Button
                  onClick={async () => {
                    setBatchUpdating(true);
                    setBatchResult(null);
                    try {
                      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
                      const body: any = { settings: batchSettings };
                      if (selectedBotIds.size > 0) {
                        body.bot_ids = Array.from(selectedBotIds);
                      } else {
                        body.filters = batchFilters;
                      }
                      const res = await fetch('/signal/batch_update', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                        body: JSON.stringify(body)
                      });
                      const data = await res.json();
                      setBatchResult({ message: data.message, ok: data.ok });
                      if (data.ok) {
                        if (selectedBotIds.size > 0) setSelectedBotIds(new Set());
                        setTimeout(() => { router.reload({ only: ['initial_bots'] }); }, 1500);
                      }
                    } catch (err: any) {
                      setBatchResult({ message: '요청 실패: ' + err.message, ok: false });
                    } finally {
                      setBatchUpdating(false);
                    }
                  }}
                  disabled={batchUpdating || Object.keys(batchSettings).every(k => batchSettings[k] === '' || batchSettings[k] === undefined)}
                  className="flex-1 h-9 text-xs font-bold rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {batchUpdating ? '처리 중...' : (selectedBotIds.size > 0 ? '선택 적용' : '일괄 적용')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Selection Dialog */}
      {filterDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-200" onClick={() => setFilterDialogOpen(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-card z-10 flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-black flex items-center gap-2">
                <Filter className="w-4 h-4 text-primary" />
                조건으로 봇 선택
              </h2>
              <button onClick={() => setFilterDialogOpen(false)} className="p-1 hover:bg-muted rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* 이름/심볼 검색 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">이름 / 심볼 검색</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    value={filterCriteria.name}
                    onChange={(e) => setFilterCriteria(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="봇 이름 또는 심볼 입력..."
                    className="pl-8 h-9 text-xs font-bold"
                  />
                </div>
              </div>

              {/* 코인별 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">코인</Label>
                <div className="flex flex-wrap gap-1.5">
                  {batchFilterOptions.symbols.map(sym => (
                    <button
                      key={sym}
                      onClick={() => toggleFilterItem('symbols', sym)}
                      className={`text-[9px] font-bold px-2.5 py-1 rounded-lg border transition-all ${
                        filterCriteria.symbols.includes(sym)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {sym}
                    </button>
                  ))}
                </div>
              </div>

              {/* 전략별 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">전략</Label>
                <div className="flex flex-wrap gap-1.5">
                  {batchFilterOptions.strategies.map(st => (
                    <button
                      key={st}
                      onClick={() => toggleFilterItem('strategies', st)}
                      className={`text-[9px] font-bold px-2.5 py-1 rounded-lg border transition-all ${
                        filterCriteria.strategies.includes(st)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {st === 'hull_macd' ? 'H-MACD' : st === 'zerolag_macd' ? 'ZL-MACD' : st === 'cen_ichi_kewma' ? 'CenIchi' : st === 'ma_angle_breakout' ? 'MA-Angle' : st === 'auto4_signal' ? 'Auto4' : st === 'hybrid_pullback' ? 'Pullback' : st}
                    </button>
                  ))}
                </div>
              </div>

              {/* 시간대별 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">시간대</Label>
                <div className="flex flex-wrap gap-1.5">
                  {batchFilterOptions.intervals.map(iv => (
                    <button
                      key={iv}
                      onClick={() => toggleFilterItem('intervals', iv)}
                      className={`text-[9px] font-bold px-2.5 py-1 rounded-lg border transition-all ${
                        filterCriteria.intervals.includes(iv)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {iv}
                    </button>
                  ))}
                </div>
              </div>

              {/* 포지션 유무 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">포지션</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilterCriteria(prev => ({ ...prev, position: prev.position === 'yes' ? '' : 'yes' }))}
                    className={`text-[9px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                      filterCriteria.position === 'yes'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    있음
                  </button>
                  <button
                    onClick={() => setFilterCriteria(prev => ({ ...prev, position: prev.position === 'no' ? '' : 'no' }))}
                    className={`text-[9px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                      filterCriteria.position === 'no'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    없음
                  </button>
                </div>
              </div>

              {/* 보관 여부 */}
              <div className="space-y-2">
                <Label className="text-[10px] font-black text-muted-foreground uppercase">보관</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilterCriteria(prev => ({ ...prev, archived: prev.archived === 'yes' ? '' : 'yes' }))}
                    className={`text-[9px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                      filterCriteria.archived === 'yes'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    보관됨
                  </button>
                  <button
                    onClick={() => setFilterCriteria(prev => ({ ...prev, archived: prev.archived === 'no' ? '' : 'no' }))}
                    className={`text-[9px] font-bold px-3 py-1.5 rounded-lg border transition-all ${
                      filterCriteria.archived === 'no'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    미보관
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 h-9 text-xs font-bold rounded-xl"
                  onClick={() => { setFilterCriteria({ name: '', symbols: [], strategies: [], intervals: [], position: '', archived: '' }); setFilterDialogOpen(false); }}
                >
                  취소
                </Button>
                <Button
                  onClick={applyFiltersAndClose}
                  className="flex-1 h-9 text-xs font-bold rounded-xl bg-primary hover:bg-primary/90"
                >
                  <CheckSquare className="w-3 h-3 mr-1" /> 조건 적용
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav activeTab={activeTab} setActiveTab={handleNavChange} current_user={current_user} />

      {current_user?.logout_path && (
        <div className="max-w-md mx-auto mt-4 pb-12 flex flex-col items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
          {(current_user.name || current_user.email) && (
            <a
              href="/dca#profile"
              className="text-[10px] font-bold text-muted-foreground/60 hover:text-primary transition-colors tracking-tight"
            >
              {current_user.name ? `${current_user.name} (${current_user.email})` : current_user.email}
            </a>
          )}
          <div className="flex items-center gap-3">
            {current_user.is_admin && (
              <a
                href="/admin"
                className="text-[10px] font-black text-muted-foreground/60 hover:text-primary transition-colors uppercase tracking-widest flex items-center gap-1"
              >
                <Shield className="w-3 h-3" /> /admin
              </a>
            )}
            {current_user.is_impersonated && (
              <a
                href="?user_id=clear"
                className="text-[11px] font-black text-amber-500 hover:text-amber-600 transition-colors uppercase tracking-widest"
              >
                돌아가기 (Exit Impersonation)
              </a>
            )}
            <button
              type="button"
              onClick={() => router.delete(current_user.logout_path)}
              className="text-[11px] font-black text-muted-foreground/80 hover:text-danger transition-colors uppercase tracking-widest"
            >
              Sign Out / 로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
